/**
 * Resolves the raw value of a variant/product attribute, preferring the
 * "_v1" suffixed version of the key (if present) over the legacy key.
 *
 * e.g. getAttributeValue(attributes, 'color_dd') looks up
 * `attributes.color_dd_v1` first, falling back to `attributes.color_dd`.
 *
 * Passing an already-"_v1" key (e.g. 'trim_cards_v1') is also supported and
 * normalized to the same lookup order.
 */
export const getAttributeValue = (attributes: Record<string, unknown> | undefined, key: string): unknown => {
  if (!attributes || !key) {
    return undefined;
  }

  const normalizedKey = key.replace(/_v1$/, '');
  const attributeKeys = Array.from(new Set([`${normalizedKey}_v1`, normalizedKey]));

  for (const attributeKey of attributeKeys) {
    const value = attributes[attributeKey];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return undefined;
};

/**
 * Safely resolves a string attribute value and lowercases it.
 * Returns an empty string when the attribute is missing or not a string-like value.
 */
export const getAttributeValueLowerCase = (attributes: Record<string, unknown> | undefined, key: string): string => {
  const value = getAttributeValue(attributes, key);

  if (typeof value === 'string') {
    return value.toLowerCase();
  }

  if (value && typeof value === 'object' && 'label' in value && typeof value.label === 'string') {
    return value.label.toLowerCase();
  }

  return '';
};

/**
 * Resolves which facet/data keys (e.g. keys of a search facets response like
 * `variants.attributes.size_pp` or `variants.attributes.size_pp_v1`) should
 * be used for a configured facet key, preferring any key matching the "_v1"
 * suffixed form over the legacy form — mirroring getAttributeValue's
 * "_v1"-first fallback, but for matching against a list of candidate keys
 * instead of a single attributes object.
 *
 * e.g. resolveFacetKeyMatches(['variants.attributes.size_pp_v1'], 'variants.attributes.size_pp')
 * returns ['variants.attributes.size_pp_v1'].
 *
 * If no "_v1" match is found among dataKeys, falls back to keys matching the
 * legacy (non-"_v1") facet key. Passing an already-"_v1" facet key is also
 * supported and normalized to the same lookup order.
 */
export const resolveFacetKeyMatches = (dataKeys: string[], facetKey: string): string[] => {
  if (!facetKey) {
    return [];
  }

  const normalizedKey = facetKey.replace(/_v1$/, '');
  const v1Key = `${normalizedKey}_v1`;

  const v1Matches = dataKeys.filter((key) => key.includes(v1Key));
  if (v1Matches.length > 0) {
    return v1Matches;
  }

  return dataKeys.filter((key) => key.includes(normalizedKey));
};

/**
 * Extracts a scalar label/value from a CT attribute, with "_v1"-first
 * fallback support.
 *
 * Two call forms:
 *  - getAttributeLabel(attributeValue): extracts the label from an already
 *    resolved attribute value (string/number/boolean, or `{ label }` enum
 *    object).
 *  - getAttributeLabel(attributes, key): looks up `key` on `attributes`
 *    object with "_v1"-first fallback (via getAttributeValue), then
 *    extracts its label. Equivalent to
 *    getAttributeLabel(getAttributeValue(attributes, key)).
 */
export const getAttributeLabel = (
  value: unknown,
  attributeKey?: string,
  attributes?: Record<string, unknown>,
): string | number | boolean | undefined => {
  if (attributeKey) {
    const source =
      attributes ??
      (value && typeof value === 'object' && !Array.isArray(value) && !('label' in value)
        ? (value as Record<string, unknown>)
        : undefined);

    if (source) {
      return getAttributeLabel(getAttributeValue(source, attributeKey));
    }
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value && typeof value === 'object' && 'label' in value) {
    const label = value.label;
    if (typeof label === 'string' || typeof label === 'number' || typeof label === 'boolean') {
      return label;
    }
  }

  return undefined;
};
